import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { errorMessage } from '@helpers/Constants';
import { Assets } from '@helpers/Constants/Assets';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { useNavigate } from 'react-router-dom';

export const SubscriptionFailed: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  /**
   * createCheckoutSession
   *
   * Creates a new checkout session for subscription payment using stored data
   * and redirects the user to the payment page.
   *
   * Workflow:
   * - Retrieves the previously stored checkout session payload from localStorage.
   * - Sends a request to create a new subscription/checkout session.
   * - On success:
   *    - Redirects the user to the Stripe checkout/payment page.
   *    - Stores the new session ID in localStorage for future verification.
   *
   * Dependencies:
   * - Axios for API calls
   * - ApiEndpoints.CREATE_SUBSCRIPTION for endpoint reference
   * - localStorage for retrieving and storing session data
   * - showToastMessage for user notifications
   *
   * @async
   * @function createCheckoutSession
   * @returns {Promise<void>}
   */

  const createCheckoutSession = async () => {
    // Get the data required to create the checkout session from the local storage
    const createCheckoutSession = JSON.parse(
      localStorage.getItem('createCheckoutSession') ?? '',
    );
    try {
      const response = await Axios.post(
        ApiEndpoints.CREATE_SUBSCRIPTION,
        createCheckoutSession,
      );

      // Navigate to the payment screen
      window.location.href = response.data.data.checkoutUrl;
      // Set the new session ID for future use
      localStorage.setItem('sessionId', response.data.data.sessionId);
    } catch {
      showToastMessage(errorMessage.DEFAULT_ERROR_MESSAGE, 'error');
    }
  };

  /**
   * handleVerifySubscription
   *
   * Verifies the current subscription status using the stored session ID
   * and determines the next step in the subscription flow.
   *
   * Workflow:
   * - Retrieves the session ID from localStorage.
   * - Sends a request to verify the subscription status.
   * - Based on the response:
   *    - If status is 1 (pending):
   *        - If no checkout URL is available, creates a new checkout session.
   *        - Otherwise, redirects the user to the existing payment page.
   *    - If status is not 1:
   *        - Redirects the user to the subscription confirmation screen.
   *
   * Dependencies:
   * - Axios for API calls
   * - ApiEndpoints.VERIFY_SUBSCRIPTION for endpoint reference
   * - localStorage for retrieving session data
   * - createCheckoutSession for regenerating checkout session
   * - navigate for routing
   * - FrontendRoutes.SUBSCRIPTION_CONFIRMATION for navigation target
   * - showToastMessage for user notifications
   *
   * @async
   * @function handleVerifySubscription
   * @returns {Promise<void>}
   */
  const handleVerifySubscription = async () => {
    // Get the session from the local storage for verification
    const sessionId = localStorage.getItem('sessionId');
    try {
      const response = await Axios.post(ApiEndpoints.VERIFY_SUBSCRIPTION, {
        session_id: sessionId,
      });
      // When the Status is either 1 or 2 - Plan is Active and 0 - Subscription Pending
      if (response.data.data.status == 0) {
        if (response.data.data.session.url == null) {
          // Create a new session when the checkout url is null and the status is 1
          createCheckoutSession();
        } else {
          // Navigate to payment page when the status is 1 (Pending)
          window.location.href = response.data.data.session.url;
        }
      } else {
        // Navigate to Subscritpion activation success screen when the status is other than 1
        navigate(FrontendRoutes.SUBSCRIPTION_CONFIRMATION);
      }
    } catch {
      showToastMessage(errorMessage.DEFAULT_ERROR_MESSAGE, 'error');
    }
  };

  return (
    <div className="h-screen flex flex-col items-center justify-center relative px-4">
      <div className="flex flex-col items-end gap-2 p-3">
        <button
          type="button"
          onClick={() => navigate(FrontendRoutes.VIEW_PLANS)}
          className="absolute top-4 right-4 text-xl text-gray-500 hover:text-gray-800"
        >
          ✕
        </button>
      </div>
      <div className="w-full max-w-md flex flex-col items-center gap-4 text-center">
        <span className="text-[#D94335] text-4xl">Payment Failed</span>

        <img
          src={Assets.CONFIRMATION_FAILURE}
          alt="Failure"
          className="w-24 h-24 object-contain"
        />

        <h2 className="text-xl text-[#7F7F7F] font-semibold font-manrope">
          Unfortunately your payment was rejected
        </h2>

        <button
          type="submit"
          onClick={handleVerifySubscription}
          className="mt-4 w-1/2 px-6 py-2 rounded-full font-medium text-white bg-[#EA5455] hover:bg-dark-primary"
        >
          Try Again
        </button>
      </div>
    </div>
  );
};
